import React, { Component } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Heading, Page, LayoutSingleColumn, NamedLink } from '../../components';
import { categorySearchString } from '../../components/CategoryMenu/helpers';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import HeroSearchForm from '../LandingPage/sections/SectionHero/HeroSearchForm/HeroSearchForm';

import css from './NotFoundPage.module.css';

export class NotFoundPageComponent extends Component {
  constructor(props) {
    super(props);
    // The StaticRouter component used in server side rendering
    // provides the context object. We attach a `notfound` flag to
    // the context to tell the server to change the response status
    // code into a 404. A listing its seller deleted is gone for good: 410.
    if (this.props.staticContext) {
      if (this.props.isGone) {
        this.props.staticContext.gone = true;
      } else {
        this.props.staticContext.notfound = true;
      }
    }
  }

  render() {
    const { marketplaceName, intl, scrollingDisabled, isGone, goneCategory } = this.props;

    const title = intl.formatMessage({
      id: isGone ? 'NotFoundPage.goneTitle' : 'NotFoundPage.title',
    });

    return (
      <Page title={title} scrollingDisabled={scrollingDisabled} noIndex={isGone}>
        <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
          <div className={css.root}>
            <div className={css.content}>
              {isGone ? null : <div className={css.number}>404</div>}
              <Heading as="h1" rootClassName={css.heading}>
                <FormattedMessage
                  id={isGone ? 'NotFoundPage.goneHeading' : 'NotFoundPage.heading'}
                />
              </Heading>
              <p className={css.description}>
                <FormattedMessage
                  id={isGone ? 'NotFoundPage.goneDescription' : 'NotFoundPage.description'}
                  values={{ marketplaceName }}
                />
              </p>
              {isGone ? (
                <div className={css.goneLinkWrapper}>
                  <NamedLink
                    className={css.goneLink}
                    name="SearchPage"
                    to={{ search: categorySearchString(goneCategory?.path) }}
                  >
                    {goneCategory ? (
                      <FormattedMessage
                        id="NotFoundPage.goneCategoryLink"
                        values={{ category: goneCategory.name }}
                      />
                    ) : (
                      <FormattedMessage id="NotFoundPage.goneAllListingsLink" />
                    )}
                  </NamedLink>
                </div>
              ) : null}
              <div className={css.searchForm}>
                <HeroSearchForm />
              </div>
            </div>
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }
}

/**
 * The not-found page.
 *
 * @param {Object} props
 * @param {Object} props.staticContext - The context object from StaticRouter. It should be manually passed if this page is rendered inside other route.
 * @param {boolean} props.scrollingDisabled - Whether the scrolling is disabled
 * @param {string} props.marketplaceName - The marketplace name
 * @param {boolean} props.isKeywordSearch - Whether the search is a keyword search
 * @param {boolean} [props.isGone] - A listing deleted by its seller: says so, answers 410 and
 *   keeps the page out of search engines
 * @param {{ path: Array<string>, name: string }} [props.goneCategory] - With `isGone`: the
 *   deleted listing's category, for the "Se flere …" link. Without one the link goes to all
 *   listings.
 * @returns {JSX.Element} Enhanced not found page component
 */
const EnhancedNotFoundPage = props => {
  const config = useConfiguration();
  const intl = useIntl();

  return <NotFoundPageComponent marketplaceName={config.marketplaceName} intl={intl} {...props} />;
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const NotFoundPage = compose(connect(mapStateToProps))(EnhancedNotFoundPage);

export default NotFoundPage;
